import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useT } from '@transifex/react';
import { isEqual } from 'lodash';

import workspacesReducer from '@app/reducers/entities/workspaces';
import { getAccountFromSessionSelector } from '@app/reducers/session';
import { usePlaybookStore } from '@app/store/zustand-store/use-playbook-store';
import { amplitudeLogEvent } from '@flow/libs/client-analytics/amplitude';
import { T } from '@app/components/translation';

import CircularSpinner from '@app/components/icons/circular-spinner';
import Button from '@app/components/button';
import adminPage from '@app/hocs/admin-page';
import EditIcon from '@app/components/icons/edit';
import CreateContractIcon from '@app/components/icons/create-contract';
import EmptyState from '@app/components/empty-state';
import { CancelButton } from '@app/components/buttons';
import { unknownApiError } from '@app/components/api-error';
import { DisableAiReview } from '@app/components/modals/disable-ai-review';
import toast from '@app/components/toasts';

import { ConceptCard } from './concept-card';
import { LanguageWarning } from './language-warning';
import { ClauseSelect } from './clause-select';

import style from './ai-review.module.scss';

type Params = {
  id: string;
};

export const AiReview = () => {
  const t = useT();
  const dispatch = useDispatch();
  const { id } = useParams<Params>();
  const updateState = useSelector((state) =>
    workspacesReducer.getUpdateWorkspacePlaybookSelector(state, {
      id: Number(id),
    }),
  );
  const workspace = useSelector((state) =>
    workspacesReducer.getWorkspaceSelector(state, {
      id: Number(id),
    }),
  );
  const { loading } = useSelector((state) =>
    workspacesReducer.getGetWorkspacePlaybookSelector(state, {
      id: Number(id),
    }),
  );
  const account = useSelector(getAccountFromSessionSelector);
  const setPlaybookValues = usePlaybookStore((state) => state.setPlaybookValues);
  const playbookValues = usePlaybookStore((state) => state.playbookValues);
  const initializeDefaultValues = usePlaybookStore((state) => state.initializeDefaultValues);
  const resetPlaybookValues = usePlaybookStore((state) => state.resetPlaybookValues);
  const initializeResponseData = usePlaybookStore((state) => state.initializeResponseData);
  const playbookEnabled = usePlaybookStore((state) => state.playbookEnabled);
  const setPlaybookEnabled = usePlaybookStore((state) => state.setPlaybookEnabled);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const savedPlaybookValues = useRef(null);
  const isCreatingNewPlaybook = useRef(false);
  const isSubmitDisabled = useMemo(
    () => isEqual(savedPlaybookValues.current, playbookValues) && playbookEnabled,
    [
      playbookValues,
      playbookEnabled,
    ],
  );
  const isDisablingWorkspace = useMemo(
    () =>
      playbookValues
        ? Object.entries(playbookValues).filter(([, values]) => values.enabled).length === 0
        : false,
    [
      playbookValues,
    ],
  );

  useEffect(() => {
    return () => {
      resetPlaybookValues();
    };
  }, [
    resetPlaybookValues,
  ]);

  const disableAiReview = useCallback(() => {
    dispatch(
      workspacesReducer.updateWorkspacePlaybook({
        id: Number(id),
        data: {
          enabled: false,
        },
        pipe: {
          onSuccess: () => {
            setIsEditMode(false);
            setPlaybookEnabled(false);
          },
        },
      }),
    );
    amplitudeLogEvent('Disable Workspace Playbook', {
      'workspace id': Number(id),
    });
  }, [
    dispatch,
    id,
    setPlaybookEnabled,
  ]);

  useEffect(() => {
    dispatch(
      workspacesReducer.getWorkspacePlaybook({
        id: Number(id),
        pipe: {
          onSuccess: (response) => {
            savedPlaybookValues.current = response.playbookValues;
            if (!response.playbookValues) {
              isCreatingNewPlaybook.current = true;
            }
            initializeResponseData(response, workspace.brandingCountry || account.country);
          },
          onFailure: () => {
            toast.error({
              id: 'playbook-fetch-error',
              title: unknownApiError.headerText,
              description: unknownApiError.bodyText,
            });
          },
        },
      }),
    );
  }, [
    dispatch,
    id,
    account.country,
    workspace.brandingCountry,
    initializeResponseData,
  ]);

  const onCancel = useCallback(() => {
    if (savedPlaybookValues.current) {
      setPlaybookValues(savedPlaybookValues.current);
    }
    setIsEditMode(false);
  }, [
    setPlaybookValues,
  ]);

  const handleConfirm = useCallback(() => {
    const data = {
      enabled: true,
      playbookValues,
    };

    if (isCreatingNewPlaybook.current) {
      dispatch(
        workspacesReducer.createWorkspacePlaybook({
          id: Number(id),
          data: {
            ...data,
            accountId: workspace.account,
          },
          pipe: {
            onSuccess: () => {
              isCreatingNewPlaybook.current = false;
              setIsEditMode(false);
              setPlaybookEnabled(true);
            },
            onFailure: () => {
              toast.error({
                id: 'playbook-create-error',
                title: unknownApiError.headerText,
                description: unknownApiError.bodyText,
              });
            },
          },
        }),
      );
    } else {
      dispatch(
        workspacesReducer.updateWorkspacePlaybook({
          id: Number(id),
          data,
          pipe: {
            onSuccess: () => {
              setIsEditMode(false);
              setPlaybookEnabled(true);
            },
            onFailure: () => {
              toast.error({
                id: 'playbook-update-error',
                title: unknownApiError.headerText,
                description: unknownApiError.bodyText,
              });
            },
          },
        }),
      );
    }

    amplitudeLogEvent('Enable Workspace Playbook', {
      'workspace id': Number(id),
    });
  }, [
    dispatch,
    workspace.account,
    id,
    playbookValues,
    setPlaybookEnabled,
  ]);

  const onEnableClick = useCallback(() => {
    const country = workspace.brandingCountry || account.country;
    initializeDefaultValues(workspace.type, country, t);
    setIsEditMode(true);
    if (isCreatingNewPlaybook.current) {
      toast.info({
        id: 'playbook-created',
        title: (
          <T
            _str="We've created an initial playbook for you"
            _comment="Toast message when playbook is created"
          />
        ),
        description: (
          <T
            _str="You can add, edit or remove clauses to build your own AI playbook"
            _comment="Toast message when playbook is created"
          />
        ),
        duration: 5000,
      });
    }
  }, [
    workspace.brandingCountry,
    account.country,
    workspace.type,
    initializeDefaultValues,
    t,
  ]);

  if (loading) {
    return (
      <div className={style.Loading}>
        <CircularSpinner />
      </div>
    );
  }

  return (
    <div className={style.AiReviewContainer}>
      <div className={style.EditActions}>
        {isEditMode ? (
          <>
            {isDisablingWorkspace ? (
              <DisableAiReview
                disableAiReview={disableAiReview}
                workspace={workspace}
                updateState={updateState}
                withConfirmButton
              />
            ) : (
              <Button
                onClick={handleConfirm}
                kind="primary"
                type="submit"
                disabled={isSubmitDisabled}
              >
                <T
                  _str="Confirm"
                  _comment="CTA to confirm changes made in a form"
                />
              </Button>
            )}

            <CancelButton onClick={onCancel} />
          </>
        ) : (
          <>
            {!playbookEnabled ? (
              <Button
                kind="primary"
                onClick={onEnableClick}
              >
                <T
                  _str="Enable"
                  _comment="Used as the text of the relevant button in details page"
                />
              </Button>
            ) : (
              <>
                <Button
                  kind="primary"
                  onClick={() => setIsEditMode(true)}
                  icon={EditIcon}
                >
                  <T
                    _str="Edit"
                    _comment="Used as the text of the relevant button in details page."
                  />
                </Button>
                {!isEditMode && (
                  <DisableAiReview
                    disableAiReview={disableAiReview}
                    workspace={workspace}
                    updateState={updateState}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      {playbookEnabled && <LanguageWarning className={style.InEditMode} />}
      {!playbookEnabled && !isEditMode ? (
        <EmptyState
          icon={<CreateContractIcon height="33px" />}
          header="AI Review"
          content={
            <div className={style.EmptyStateContent}>
              <div>
                <T
                  future
                  _str="By enabling this feature, you are able to set guidelines such as payment terms or governing laws specifically for this workspace."
                  _comment="Empty state text for the AI Review page when not enabled."
                />
              </div>
              <LanguageWarning />
            </div>
          }
          defaultStyle
          className={style.Empty}
        />
      ) : (
        <div
          style={{
            padding: 32,
          }}
        >
          <p className={style.HeaderText}>
            AI Review{' '}
            <T
              _str="playbook"
              _comment="Header of the AI Review page."
            />
          </p>
          <p>
            <T
              _str="AI Review will scan your legal documents for the following clauses:"
              _comment="Label for the select field in the AI Review page."
            />
          </p>
          <ClauseSelect isEditMode={isEditMode} />
          {playbookValues
            ? Object.entries(playbookValues)
                .filter(([, values]) => values.enabled)
                .map(([key, values]) => (
                  <ConceptCard
                    key={key}
                    name={key}
                    values={values}
                    areActionsVisible={isEditMode}
                  />
                ))
            : null}
        </div>
      )}
    </div>
  );
};

export const propsMapper = () => ({
  title: 'AI Review',
  modules: [
    [],
  ],
});

export default adminPage(propsMapper)(AiReview);
